import type { AppLocale } from '@/lib/i18n'

/** Local wall-clock value used by the form: `YYYY-MM-DDTHH:mm`. */
export interface LocalDateTimeParts {
  date: string
  time: string
}

export function splitLocalDateTime(value: string): LocalDateTimeParts {
  const [date = '', time = ''] = value.split('T')
  return { date, time: time.slice(0, 5) }
}

export function formatDisplayDate(
  date: string,
  locale: AppLocale,
): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date)
  if (!match) return ''
  const [, year, month, day] = match
  return locale === 'it'
    ? `${day}/${month}/${year}`
    : `${month}/${day}/${year}`
}

/**
 * Parses the visible date in the active locale and returns the canonical
 * `YYYY-MM-DD` value. Both slash and dot separators are accepted so pasting
 * from either locale keeps working.
 */
export function parseDisplayDate(
  value: string,
  locale: AppLocale,
): string | null {
  const trimmed = value.trim()
  if (!trimmed) return null

  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed)
  if (iso) {
    const [, year, month, day] = iso
    return validDate(year, month, day) ? `${year}-${month}-${day}` : null
  }

  const match = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(trimmed)
  if (!match) return null
  const first = match[1]
  const second = match[2]
  const year = match[3]
  const day = locale === 'it' ? first : second
  const month = locale === 'it' ? second : first
  const dayPadded = day.padStart(2, '0')
  const monthPadded = month.padStart(2, '0')
  return validDate(year, monthPadded, dayPadded)
    ? `${year}-${monthPadded}-${dayPadded}`
    : null
}

function validDate(year: string, month: string, day: string): boolean {
  const y = Number(year)
  const m = Number(month)
  const d = Number(day)
  if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) {
    return false
  }
  const date = new Date(y, m - 1, d)
  return (
    date.getFullYear() === y &&
    date.getMonth() === m - 1 &&
    date.getDate() === d
  )
}

export function datePlaceholder(locale: AppLocale): string {
  return locale === 'it' ? 'gg/mm/aaaa' : 'mm/dd/yyyy'
}

export function timePlaceholder(): string {
  return 'HH:mm'
}

/** Parses a 24-hour `HH:mm` (or `H:mm`) value; returns `HH:mm`. */
export function parseTimeInput(value: string): string | null {
  const match = /^(\d{1,2}):?(\d{2})$/.exec(value.trim())
  if (!match) return null
  const hours = Number(match[1])
  const minutes = Number(match[2])
  if (
    !Number.isInteger(hours) ||
    !Number.isInteger(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return null
  }
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}
